import {
  Editor,
  getNodeList,
  Node,
  Plugin,
  StringNode,
  updateErrors,
  UseEditorOptions,
} from '@sagold/react-json-editor';
import { DateTime } from 'luxon';

import { dateFormatter } from '../../components/table/valueFormatters';

import {
  getCustomNode,
  getNodeFromJsonPath,
  isChangeEvent,
} from './pluginHelper';

export const dateDraftConfig: UseEditorOptions['draftConfig'] = {
  validateFormat: {
    date: (node, value) => {
      if (typeof value !== 'string') {
        return;
      }

      const nodeDate = DateTime.fromISO(value);
      if (!nodeDate.isValid) {
        return;
      }

      const { minDate, maxDate } = node.schema.options || {};

      // Helper function to create error objects
      const createDateError = (code: string, compareValue?: string) => ({
        type: 'error' as const,
        code,
        message: '',
        path: node.pointer,
        name: '',
        compareValue,
        data: {
          pointer: node.pointer,
          schema: node.schema,
          value,
        },
      });

      // Validate minimum date
      if (typeof minDate === 'string') {
        const minDateObj = DateTime.fromISO(minDate);
        if (minDateObj.isValid && nodeDate < minDateObj) {
          return createDateError(
            'custom-min-date-error',
            `${dateFormatter(minDateObj?.toISO(), {})}`
          );
        }
      }

      // Validate maximum date
      if (typeof maxDate === 'string') {
        const maxDateObj = DateTime.fromISO(maxDate);
        if (maxDateObj.isValid) {
          if (nodeDate > maxDateObj) {
            return createDateError(
              'custom-max-date-error',
              `${dateFormatter(maxDateObj?.toISO(), {})}`
            );
          }
          if (node.schema.options.shouldBeMax && nodeDate < maxDateObj) {
            return createDateError('custom-should-be-max-date-error');
          }
        }
      }

      return;
    },
  },
};

const setCustomDateErrors = (
  node: StringNode,
  rootNode: Node,
  draft: Editor['draft']
) => {
  if (!node.schema.options) {
    return;
  }
  const { minDateRef, maxDateRef } = node.schema.options;

  if (typeof minDateRef === 'string') {
    const minDateNode = getNodeFromJsonPath(
      node.pointer,
      minDateRef,
      rootNode
    ) as StringNode;
    if (minDateNode && minDateNode.value) {
      const minDateObj = DateTime.fromISO(minDateNode.value);
      if (minDateObj.isValid) {
        node.schema.options.minDate = minDateObj.toISODate();
      }
    }
  }

  if (typeof maxDateRef === 'string') {
    const maxDateNode = getNodeFromJsonPath(
      node.pointer,
      maxDateRef,
      rootNode
    ) as StringNode;
    if (maxDateNode && maxDateNode.value) {
      const minDateObj = DateTime.fromISO(maxDateNode.value);
      if (minDateObj.isValid) {
        node.schema.options.maxDate = minDateObj.toISODate();
      }
    }
  }
  updateErrors(draft, node);
};

const setMaxOfDate = (node: StringNode, root: Node) => {
  const lookUpArrNode = getNodeFromJsonPath(
    node.pointer,
    node.schema.updateToMaxOf,
    root
  );
  if (lookUpArrNode == null) {
    return;
  }
  const arrayOfNodes = getNodeList(lookUpArrNode).filter(
    (n) => n.property === node.property
  ) as StringNode[];
  let maxDate = 0;
  for (const n of arrayOfNodes) {
    if (n.value) {
      const nDate = DateTime.fromISO(n.value);
      if (nDate.isValid) {
        maxDate = Math.max(maxDate, nDate.toMillis());
      }
    }
  }
  if (maxDate > 0) {
    const newDate = DateTime.fromMillis(maxDate);
    if (newDate.isValid) {
      node.value = newDate.toISODate();
    }
  }
};

/**
 * This plugin directly modifies the error node value of react-json-editor, as rje does not have min max date validation by default
 */
export const DateValidationPlugin: Plugin = (he) => {
  return {
    id: 'custom-date-validation-plugin',
    onEvent(root, event) {
      if (!isChangeEvent(event)) {
        return undefined;
      }
      for (const node of getCustomNode(root, 'updateToMaxOf')) {
        if (node.type === 'string' && node.schema.format === 'date') {
          setMaxOfDate(node, root);
        }
      }
      for (const node of getNodeList(root)) {
        if (
          node.type === 'string' &&
          node.schema.format === 'date' &&
          node.options?.showErrorWithColor
        ) {
          setCustomDateErrors(node, root, he.draft);
        }
      }
      return [root, []];
    },
  };
};
